<template>
  <q-dialog
    :model-value="modelValue"
    position="bottom"
    @update:model-value="(v) => emit('update:modelValue', v)">
    <q-card class="bottom-nav-more">
      <div class="bottom-nav-more__handle" />
      <q-list>
        <template
          v-for="group in grouped"
          :key="group.category">
          <q-item-label
            header
            class="bottom-nav-more__category">
            {{ categoryLabel(group.category) }}
          </q-item-label>
          <q-item
            v-for="item in group.items"
            :key="item.id"
            v-close-popup
            clickable
            @click="handleItemClick(item)">
            <q-item-section avatar>
              <q-spinner
                v-if="loadingId === item.id"
                :size="22" />
              <q-icon
                v-else-if="item.icon"
                :name="item.icon" />
            </q-item-section>
            <q-item-section>{{ item.label }}</q-item-section>
          </q-item>
          <q-separator />
        </template>
      </q-list>
    </q-card>
  </q-dialog>
</template>

<script setup lang="ts">
import { downloadReport } from 'src/CrudManager/Core/Utils/downloadReport'
import type { BottomNavSourceItem } from 'src/composables/useBottomNavSource'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

const props = defineProps<{
  modelValue: boolean
  items: BottomNavSourceItem[]
}>()

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
}>()

const router = useRouter()
const { t, te } = useI18n()
const loadingId = ref<string | null>(null)

const CATEGORY_ORDER = ['operations', 'accounting', 'administration', 'ai', 'reports', 'tools', 'system']

const grouped = computed<{ category: string; items: BottomNavSourceItem[] }[]>(() => {
  const buckets = new Map<string, BottomNavSourceItem[]>()
  for (const item of props.items) {
    const key = item.category || 'operations'
    const list = buckets.get(key) ?? []
    list.push(item)
    buckets.set(key, list)
  }
  const keys = Array.from(buckets.keys()).sort((a, b) => {
    const ai = CATEGORY_ORDER.indexOf(a)
    const bi = CATEGORY_ORDER.indexOf(b)
    if (ai !== -1 && bi !== -1) return ai - bi
    if (ai !== -1) return -1
    if (bi !== -1) return 1
    return a.localeCompare(b)
  })
  return keys.map((category) => ({ category, items: buckets.get(category)! }))
})

const categoryLabel = (category: string): string => {
  const key = `crudmanager.categories.${category}`
  return te(key) ? t(key) : category
}

const handleItemClick = async (item: BottomNavSourceItem) => {
  if (item.kind === 'download') {
    if (!item.downloadUrl || loadingId.value) return
    loadingId.value = item.id
    try {
      await downloadReport({
        url: item.downloadUrl,
        fallbackFilename: item.filename,
        successMessage: t('crudmanager.customMenu.download_success'),
        errorMessage: t('crudmanager.customMenu.download_error'),
        progressMessage: t('crudmanager.customMenu.download_progress'),
      })
    } finally {
      loadingId.value = null
    }
    return
  }
  if (item.to) void router.push(item.to)
}
</script>

<style lang="scss" scoped>
.bottom-nav-more {
  width: 100%;
  max-height: 70vh;
  overflow-y: auto;
  padding-bottom: env(safe-area-inset-bottom, 0px);
  border-radius: 16px 16px 0 0;
}

.bottom-nav-more__handle {
  width: 36px;
  height: 4px;
  margin: 8px auto 4px;
  border-radius: 2px;
  background: rgba(0, 0, 0, 0.18);
}

.body--dark .bottom-nav-more__handle {
  background: rgba(255, 255, 255, 0.25);
}

.bottom-nav-more__category {
  text-transform: uppercase;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  color: var(--q-primary);
  padding: 12px 16px 4px;
}
</style>
